import { Injectable } from '@angular/core';
import { UserStore } from '../../state/user.repository';
import { UserService } from './user.service';

// Latches per-user speech state for `User.isActiveSpeaker`. A user must be
// continuously active for ACTIVE_SPEAKER_ON_MS to flip on, and silent for
// ACTIVE_SPEAKER_OFF_MS to flip off. Brief pauses within the off window do
// not flip the flag — the check re-arms against the timestamp instead of
// sampling instantaneous activity. The own user raising their hand auto-
// clears when they start speaking.
@Injectable({ providedIn: 'root' })
export class ActiveSpeakerTrackerService {
  private readonly ACTIVE_SPEAKER_ON_MS = 3000;
  private readonly ACTIVE_SPEAKER_OFF_MS = 6000;
  private _lastActiveAt = new Map<string, number>();
  private _streakStartedAt = new Map<string, number>();
  private _onTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private _offTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private _usersStore: UserStore,
    private _userService: UserService
  ) {}

  track(userId: string, isActive: boolean) {
    const user = this._usersStore.get(userId);
    if (!user) return;
    const now = Date.now();

    if (isActive) {
      this._lastActiveAt.set(userId, now);
      if (!this._streakStartedAt.has(userId)) {
        this._streakStartedAt.set(userId, now);
      }
      // Speech resumed — a pending off-check no longer applies.
      this._clearOffTimer(userId);
      if (!user.isActiveSpeaker && !this._onTimers.has(userId)) {
        this._scheduleOn(userId, this.ACTIVE_SPEAKER_ON_MS);
      }
    } else {
      if (user.isActiveSpeaker && !this._offTimers.has(userId)) {
        this._scheduleOff(userId, this.ACTIVE_SPEAKER_OFF_MS);
      }
      // If not yet an active speaker, the streak is broken — reset so the next
      // active event starts a fresh window.
      if (!user.isActiveSpeaker) {
        this._clearOnTimer(userId);
        this._streakStartedAt.delete(userId);
      }
    }
  }

  clear(userId: string) {
    this._clearOnTimer(userId);
    this._clearOffTimer(userId);
    this._lastActiveAt.delete(userId);
    this._streakStartedAt.delete(userId);
  }

  resetAll() {
    for (const timer of this._onTimers.values()) clearTimeout(timer);
    for (const timer of this._offTimers.values()) clearTimeout(timer);
    this._onTimers.clear();
    this._offTimers.clear();
    this._lastActiveAt.clear();
    this._streakStartedAt.clear();
  }

  private _scheduleOn(userId: string, delayMs: number) {
    const timer = setTimeout(() => {
      this._onTimers.delete(userId);
      const user = this._usersStore.get(userId);
      if (!user || user.isActiveSpeaker) return;
      const streakStart = this._streakStartedAt.get(userId);
      if (streakStart === undefined) return;
      const elapsed = Date.now() - streakStart;
      if (elapsed >= this.ACTIVE_SPEAKER_ON_MS) {
        const updatedUser = { ...user, isActiveSpeaker: true };
        if (userId === this._userService.ownUserId && user.data.hasRaisedHand) {
          updatedUser.data = { ...updatedUser.data, hasRaisedHand: false };
          this._userService.clearRaiseHandTimer();
        }
        this._usersStore.update(updatedUser);
      } else {
        this._scheduleOn(userId, this.ACTIVE_SPEAKER_ON_MS - elapsed);
      }
    }, delayMs);
    this._onTimers.set(userId, timer);
  }

  private _scheduleOff(userId: string, delayMs: number) {
    const timer = setTimeout(() => {
      this._offTimers.delete(userId);
      const user = this._usersStore.get(userId);
      if (!user || !user.isActiveSpeaker) return;
      const lastActive = this._lastActiveAt.get(userId) ?? 0;
      const silence = Date.now() - lastActive;
      if (silence >= this.ACTIVE_SPEAKER_OFF_MS) {
        this._usersStore.update({ ...user, isActiveSpeaker: false });
        this._streakStartedAt.delete(userId);
      } else {
        this._scheduleOff(userId, this.ACTIVE_SPEAKER_OFF_MS - silence);
      }
    }, delayMs);
    this._offTimers.set(userId, timer);
  }

  private _clearOnTimer(userId: string) {
    const timer = this._onTimers.get(userId);
    if (timer) {
      clearTimeout(timer);
      this._onTimers.delete(userId);
    }
  }

  private _clearOffTimer(userId: string) {
    const timer = this._offTimers.get(userId);
    if (timer) {
      clearTimeout(timer);
      this._offTimers.delete(userId);
    }
  }
}
